import * as Sentry from '@sentry/react';

import { supabase } from '@/integrations/supabase/client';

import type { CoverRead } from './certificatePrefill';
import { readCertificateCoverPeriod } from './certificateRead';
import { parseStorageUrl } from './openSignedUrl';

/**
 * certificateOnFile — the filed certificate, pulled back down so it can be read.
 *
 * THE SECOND DOOR ONTO THE COVER-DATE READ, AND IT EXISTS BECAUSE THE FIRST ONE ONLY OPENS FOR NEW
 * DOCUMENTS. The prefill reads a certificate as it is STAGED for upload, which corrects the policy
 * being recorded now and does nothing at all for the certificates already in storage — the very
 * rows the census behind that feature found contradicted by their own paper: it read 39 filed
 * certificates in all, 38 of them on active contracts. Re-uploading a document that is already on
 * file, purely to make the tab parse it, is not a thing to ask an admin to do. So the bytes come
 * back down instead.
 *
 * IT IS A FETCH AND NOTHING MORE. No database call, no write, no upload, no `certificate_url`
 * touched: a short-lived signed URL, a GET, and a `File` for the parser. What the period then does
 * to the two date fields is `certificatePrefill`'s decision and the canvas's dispatch, and the
 * CommitBar remains the only thing that writes a policy.
 *
 * AND IT NOW RUNS WITHOUT BEING ASKED, WHICH REVERSES HALF OF WHAT THIS FILE USED TO SAY. The rule
 * was: only ever on a click, because a fetch on load pulls a document down to answer a question
 * nobody asked, and the read it feeds would overwrite dates in a block the admin had not come to
 * edit. The owner ruled on 2026-09-12 that an already-filed certificate must be CHECKED and must
 * WARN, which is the question being asked of every policy view — so the first half no longer holds
 * and the second half is answered instead by the thing that changed with it: an automatic read
 * NEVER APPLIES ITS DATES. It states what the document says and offers the click that applies them.
 *
 * ONE SIGNED URL AND ONE DOWNLOAD PER POLICY VIEW, WHICH IS WHAT MAKES THAT AFFORDABLE. Both
 * surfaces run `readFiledCertificate` inside a react-query entry keyed on the stored URL, with
 * `staleTime: Infinity`, so a certificate is fetched and parsed once and re-read only when the
 * admin asks for it again or the row moves to another policy.
 *
 * THE SIGNED URL IS SHORT, AND SHORT IS THE WHOLE OF ITS PROTECTION. Sixty seconds is a fetch that
 * either happens now or has already failed. It is NOT a secret the tab keeps to itself: the string
 * goes through the browser's own network stack, so it is visible in devtools and it reaches
 * anything watching `fetch` — which is why `src/sentry.ts` strips the query off breadcrumb and span
 * URLs before they leave the page (a signed storage URL's query IS the bearer token). The bucket
 * and path come off the stored URL through the same exported parser the `Open the certificate` link
 * uses, so the two doors onto one object can never disagree about where it lives.
 *
 * ONE BREADCRUMB, AND ONLY ON A FAILURE. `certificateRead` already logs a line per read with its
 * outcome, so a success here would double-count every read that gets as far as the parser. The ends
 * this file owns are the ones that never reach it at all — an unparseable URL, a refused signature,
 * an HTTP status, a dead network, an object too big to be worth downloading — and those are
 * otherwise invisible: the canvas prints one sentence for the whole class and the thrown message
 * goes nowhere. What is recorded is a closed-set reason and nothing else: never the URL, never the
 * object's name, never its bytes.
 */

/** Long enough for one fetch that is already in flight. */
const SIGNED_URL_SECONDS = 60;

/**
 * The parser's own ceiling (`certificateRead`'s `MAX_BYTES`), applied before the download rather
 * than after it. A 30 MB scan is refused by the reader either way; checking the declared length
 * first means the tab does not spend the bytes to be told so.
 */
const MAX_BYTES = 5 * 1024 * 1024;

/** Why a fetch ended, in a closed set. No URL, no file name, no content. */
type FetchFailure = 'not-storage-url' | 'sign-failed' | `http-${number}` | 'network' | 'too-large';

function fetchFailed(reason: FetchFailure): void {
    Sentry.addBreadcrumb({
        category: 'certificate.fetch',
        level: 'warning',
        message: 'certificate on file not fetched',
        data: { reason },
    });
}

/**
 * Fetch the certificate stored at `url` and hand it back as a `File` the parser can read.
 *
 * THROWS ON EVERY ENDING BUT SUCCESS, AND THE THROW IS THE SIGNAL. There is no `fetch-failed`
 * `CoverRead` and there never was one that anything constructed: a throw here becomes react-query
 * `isError` on both surfaces, and each prints `FETCH_FAILED_NOTE` straight off it. The messages
 * below are for a developer reading a stack, not for the screen and not for Sentry — the breadcrumb
 * above carries the reason — because the screen prints one sentence for the whole class: an admin's
 * next move is the same whether the object was missing, unsignable, refused or too big.
 */
export async function fetchCertificateOnFile(url: string): Promise<File> {
    const parsed = parseStorageUrl(url);
    if (!parsed) {
        fetchFailed('not-storage-url');
        throw new Error('The certificate is not a storage object.');
    }

    const { data, error } = await supabase.storage
        .from(parsed.bucket)
        .createSignedUrl(parsed.path, SIGNED_URL_SECONDS);
    if (error || !data?.signedUrl) {
        fetchFailed('sign-failed');
        throw new Error('The certificate could not be signed for reading.');
    }

    let response: Response;
    try {
        response = await fetch(data.signedUrl);
    } catch {
        // The one ending with no status to report: the request never completed. Whatever the
        // rejection carried, it is not rethrown — a `TypeError` from `fetch` can name the URL.
        fetchFailed('network');
        throw new Error('The certificate could not be fetched.');
    }
    if (!response.ok) {
        fetchFailed(`http-${response.status}`);
        throw new Error(`The certificate could not be fetched (${response.status}).`);
    }

    // BEFORE `.blob()`, WHICH IS WHERE THE BYTES ACTUALLY ARRIVE. A missing or unparseable header
    // is not a refusal: storage sends `content-length` on these objects, and guessing against its
    // absence would refuse readable certificates to avoid downloading unreadable ones.
    const declared = Number(response.headers.get('content-length'));
    if (Number.isFinite(declared) && declared > MAX_BYTES) {
        fetchFailed('too-large');
        throw new Error('The certificate is too large to read here.');
    }

    const blob = await response.blob();
    // The basename is what the parser's own not-a-PDF check reads when the blob arrives untyped —
    // storage serves `application/octet-stream` for anything uploaded without a content type.
    const name = parsed.path.split('/').pop() || 'certificate';
    const type = /\.pdf$/i.test(name) ? 'application/pdf' : blob.type;
    return new File([blob], name, { type });
}

/**
 * ── OCR IS SPENT ON A DOCUMENT AN ADMIN JUST ACTED ON, NEVER ON A PAGE VIEW ──
 *
 * THE READ BELOW FIRES ON EVERY POLICY VIEW, WHICH IS EXACTLY WHY IT MAY NOT OCR. Opening a
 * contract asks no question about the certificate; the read is there to WARN, and a warning is not
 * worth a 300 dpi raster and a wasm pass on a document nobody was looking at. A filed scan that
 * nothing can read is answered instead by the attested filing line, which is a fact already on the
 * row (D-B56) and costs a column rather than fifteen seconds.
 *
 * SO THE EXPENSIVE ANSWER IS ON A CLICK, AND THIS FLAG IS THE CLICK. `Check again` is what an admin
 * presses after correcting the plate on the Vehicle block or replacing the paper at the insurer —
 * it already spends a second signed URL and a second download, and this is the third thing it
 * spends.
 *
 * A MODULE-LEVEL FLAG RATHER THAN A QUERY KEY, AND THE REASON IS THE SECOND SURFACE. Contract
 * setup's Insurance canvas and the policy dossier's Policy canvas share the
 * `['certificate-on-file', url]` entry, so folding `ocr` into the key would make them two entries —
 * and the dossier, which MOUNTS with that key on every policy it opens, would then start an OCR
 * pass nobody asked for.
 *
 * SO IT HOLDS THE DOCUMENT RATHER THAN A BOOLEAN, WHICH IS WHAT MAKES 'the very next read' TRUE.
 * A bare flag is URL-blind and consumed on the way IN, and both of those leak: a `Check again`
 * whose download fails spends the pass on the failed attempt and the `retry` that follows reads
 * without it, so the admin is shown the identical refusal having never been OCR'd; and a refetch
 * that coalesces into a fetch already in flight never calls the queryFn at all, leaving the flag
 * standing for the next AUTOMATIC read — the dossier's mount read, on another policy, which is
 * exactly the pass this door exists to refuse.
 */
let ocrOnNextRead: string | null = null;

/** `Check again` says which document it is asking about. Only a read of THAT one may spend OCR. */
export function ocrOnNextFiledRead(url: string): void {
    ocrOnNextRead = url;
}

/**
 * The whole of an on-file read: the bytes back down, then the parser over them.
 *
 * IT IS ONE FUNCTION SO THE TWO SURFACES CANNOT COME APART. Contract setup's Insurance canvas and
 * the policy dossier's Policy canvas both check the certificate already filed against a policy, and
 * both run this as a react-query `queryFn` keyed on the stored URL — so the fetch, the parse and
 * the two failure endings are written once. They reach it through one shared row
 * (`inline/CertificateRow.tsx`), which is what makes the one-mount rule above enforceable.
 *
 * THE FETCH IS ALLOWED TO THROW AND THE PARSE IS NOT, WHICH IS THE DIFFERENCE BETWEEN A RETRY THAT
 * BUYS SOMETHING AND ONE THAT BUYS NOTHING. A fetch that failed may well succeed a moment later —
 * a signature refused under a stale session, a dropped connection — so it is left to react-query's
 * `retry`, and the caller prints the fetch's own sentence when the retry is spent. A parse that
 * failed will fail identically on the same bytes, so it is settled here into the `unreadable` read
 * the rules already know what to do with.
 *
 * `onPhase` IS FOR THE ONE CALLER THAT CAN ACTUALLY REACH THE SCAN BRANCH. Only `Check again`
 * spends a pass on a filed document, and it is the slow wait by construction — up to the whole OCR
 * budget behind a spinner that otherwise says nothing but 'Checking the certificate on file…',
 * which is indistinguishable from a row that has hung. A read that never reaches OCR never calls
 * it, so the automatic read passes nothing and is unchanged.
 *
 * NO DECISION IS MADE HERE. `decideCertificate` compares the read against the CONTRACT'S VEHICLE,
 * which is not part of the cache key and can be corrected on another area while this entry stands —
 * so the callers re-derive the verdict at render from the cached read, and a plate typed onto the
 * vehicle record is answered without re-downloading anything.
 */
export async function readFiledCertificate(
    url: string,
    onPhase?: (phase: 'ocr') => void,
): Promise<CoverRead> {
    /*
     * CONSUMED ONCE THE BYTES ARE IN HAND, WHICH IS WHAT MAKES IT ONE-SHOT AND NOT ONE-ATTEMPT.
     * `Check again` names the document and then asks react-query to refetch; a download that fails
     * is retried, and the pass the click bought is still waiting for it. It is taken here, against
     * THIS url, so no other policy's automatic read can ever consume it.
     */
    const file = await fetchCertificateOnFile(url);
    const ocr = ocrOnNextRead === url;
    if (ocr) ocrOnNextRead = null;

    try {
        return await readCertificateCoverPeriod(file, { ocr, onPhase });
    } catch {
        return { status: 'unreadable', reason: 'failed' };
    }
}

/**
 * WHO FILED THIS CERTIFICATE ON THEIR OWN READING — the other half of the attested fact.
 *
 * `insurances.certificate_attested_by` IS A `profiles.id` AND NOT A NAME, which is the right way
 * round for a record and the wrong way round for a sentence. The standing line an admin reads
 * names a person, so the id is resolved here, at the same door the document itself comes back
 * through: `readFiledCertificate` says what the paper is, this says whose word it is on, and both
 * surfaces read them through react-query entries of their own.
 *
 * IT IS THE SAME TABLE `Settings` AND `useContractAgreements` READ, ASKED THE OTHER WAY ROUND AND
 * FOR LESS. Those two select the viewer's own profile by `user_id` and take everything a signature
 * block needs; this one is asking who SOMEBODY ELSE was, by `id`, and a sentence needs two columns
 * to say it. A missing profile, a revoked read or a null id all answer null rather than throwing,
 * because the sentence has a name for that case (`UNNAMED_ATTESTOR`) and a filing that happened is
 * a fact whether or not the person behind it can still be named.
 */
export async function fetchAttestorName(profileId: number | null): Promise<string | null> {
    if (profileId == null) return null;
    const { data, error } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('id', profileId)
        .maybeSingle();
    if (error || !data) return null;
    const name = `${data.first_name ?? ''} ${data.last_name ?? ''}`.trim();
    return name === '' ? null : name;
}
